import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "@/components/SEO";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import SafeImage from "@/components/SafeImage";
import { MODELS, TECHNICAL_INFO, DIMENSION_CATALOG, CONFIGURATOR_LIBRARY, CONFIGURATOR_OPTIONS } from "@/data/content";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  Layers3,
  Ruler,
  Wrench,
} from "lucide-react";

const getFrameByRatio = (ratio) => {
  const frames = {
    compact: { x: 350, y: 80, w: 300, h: 240 },
    medium: { x: 230, y: 80, w: 540, h: 240 },
    wide: { x: 90, y: 80, w: 820, h: 240 },
    extraWide: { x: 55, y: 80, w: 890, h: 240 },
    long: { x: 55, y: 105, w: 890, h: 185 },
    double: { x: 110, y: 48, w: 780, h: 310 },
  };

  return frames[ratio] || frames.wide;
};

const DimensionLine = ({ x1, y1, x2, y2, label, orientation = "horizontal" }) => (
  <g className="tech-sketch__dimension">
    <line x1={x1} y1={y1} x2={x2} y2={y2} />
    {orientation === "horizontal" ? (
      <>
        <line x1={x1} y1={y1 - 8} x2={x1} y2={y1 + 8} />
        <line x1={x2} y1={y2 - 8} x2={x2} y2={y2 + 8} />
        <text x={(x1 + x2) / 2} y={y1 - 12} textAnchor="middle">{label}</text>
      </>
    ) : (
      <>
        <line x1={x1 - 8} y1={y1} x2={x1 + 8} y2={y1} />
        <line x1={x2 - 8} y1={y2} x2={x2 + 8} y2={y2} />
        <text x={x1 - 22} y={(y1 + y2) / 2} textAnchor="middle" transform={`rotate(-90 ${x1 - 22} ${(y1 + y2) / 2})`}>{label}</text>
      </>
    )}
  </g>
);

const Door = ({ x, y, flip = false }) => (
  <g className="tech-sketch__door">
    <path d={flip ? `M ${x} ${y} L ${x - 58} ${y}` : `M ${x} ${y} L ${x + 58} ${y}`} />
    <path d={flip ? `M ${x} ${y} A 58 58 0 0 0 ${x - 58} ${y - 58}` : `M ${x} ${y} A 58 58 0 0 1 ${x + 58} ${y - 58}`} />
  </g>
);

const WindowMark = ({ x, y, w }) => (
  <g className="tech-sketch__window">
    <line x1={x} y1={y} x2={x + w} y2={y} />
    <line x1={x} y1={y + 8} x2={x + w} y2={y + 8} />
  </g>
);

const SanitaryIcon = ({ x, y, label = "WC" }) => (
  <g className="tech-sketch__fixture">
    <circle cx={x} cy={y} r="22" />
    <rect x={x - 28} y={y + 34} width="56" height="26" rx="8" />
    <text x={x} y={y + 92} textAnchor="middle">{label}</text>
  </g>
);

const ShowerIcon = ({ x, y }) => (
  <g className="tech-sketch__fixture">
    <rect x={x - 32} y={y - 28} width="64" height="64" rx="10" />
    <path d={`M ${x - 16} ${y - 4} C ${x + 8} ${y - 22}, ${x + 30} ${y - 8}, ${x + 14} ${y + 18}`} />
    <text x={x} y={y + 92} textAnchor="middle">Tuš</text>
  </g>
);


const escapeSvgText = (value) => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const filenameSafe = (value) => String(value || "konfiguracija")
  .toLowerCase()
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .replace(/[^a-z0-9]+/g, "-")
  .replace(/^-+|-+$/g, "") || "konfiguracija";

const planLabelByType = {
  open: "Otvoren prostor",
  living: "Boravak / soba",
  office: "Kancelarija",
  storage: "Magacin",
  workshop: "Radionica",
  guard: "Portir",
  counter: "Portirnica + šalter",
  doubleOffice: "Spojeni moduli",
  wc: "Radni prostor + WC",
  bath: "Glavni prostor + kupatilo",
  smallWc: "WC modul",
  showers: "Tuš kabine",
  multiSanitary: "Sanitarni raspored",
};

const exportDimensionLine = ({ x1, y1, x2, y2, label, orientation = "horizontal" }) => {
  const safeLabel = escapeSvgText(label);

  if (orientation === "horizontal") {
    return `
      <g class="dimension">
        <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" />
        <line x1="${x1}" y1="${y1 - 8}" x2="${x1}" y2="${y1 + 8}" />
        <line x1="${x2}" y1="${y2 - 8}" x2="${x2}" y2="${y2 + 8}" />
        <text x="${(x1 + x2) / 2}" y="${y1 - 12}" text-anchor="middle">${safeLabel}</text>
      </g>`;
  }

  return `
    <g class="dimension">
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" />
      <line x1="${x1 - 8}" y1="${y1}" x2="${x1 + 8}" y2="${y1}" />
      <line x1="${x2 - 8}" y1="${y2}" x2="${x2 + 8}" y2="${y2}" />
      <text x="${x1 - 22}" y="${(y1 + y2) / 2}" text-anchor="middle" transform="rotate(-90 ${x1 - 22} ${(y1 + y2) / 2})">${safeLabel}</text>
    </g>`;
};

const exportDoor = ({ x, y, flip = false }) => {
  const line = flip ? `M ${x} ${y} L ${x - 58} ${y}` : `M ${x} ${y} L ${x + 58} ${y}`;
  const arc = flip ? `M ${x} ${y} A 58 58 0 0 0 ${x - 58} ${y - 58}` : `M ${x} ${y} A 58 58 0 0 1 ${x + 58} ${y - 58}`;

  return `<g class="door"><path d="${line}" /><path d="${arc}" /></g>`;
};

const exportWindow = ({ x, y, w }) => `
  <g class="window">
    <line x1="${x}" y1="${y}" x2="${x + w}" y2="${y}" />
    <line x1="${x}" y1="${y + 8}" x2="${x + w}" y2="${y + 8}" />
  </g>`;

const exportSanitary = ({ x, y, label = "WC" }) => `
  <g class="fixture">
    <circle cx="${x}" cy="${y}" r="22" />
    <rect x="${x - 28}" y="${y + 34}" width="56" height="26" rx="8" />
    <text x="${x}" y="${y + 92}" text-anchor="middle">${escapeSvgText(label)}</text>
  </g>`;

const exportShower = ({ x, y }) => `
  <g class="fixture">
    <rect x="${x - 32}" y="${y - 28}" width="64" height="64" rx="10" />
    <path d="M ${x - 16} ${y - 4} C ${x + 8} ${y - 22}, ${x + 30} ${y - 8}, ${x + 14} ${y + 18}" />
    <text x="${x}" y="${y + 92}" text-anchor="middle">Tuš</text>
  </g>`;

const createPlanSvgAttachment = (layout, selectedLabels = []) => {
  const baseFrame = getFrameByRatio(layout.plan?.ratio);
  const frame = { ...baseFrame, y: baseFrame.y + 70 };
  const type = layout.plan?.type || "open";
  const hasDoor = layout.plan?.hasDoor !== false;
  const hasWindow = layout.plan?.hasWindow !== false;
  const wLabel = layout.dimensions?.split("×")?.[0]?.trim() || "6,00 m";
  const dLabel = layout.dimensions?.split("×")?.[1]?.trim() || "2,40 m";
  const title = escapeSvgText(layout.name || "RB TIM konfiguracija");
  const dimensions = escapeSvgText(layout.dimensions || "");
  const roomLabel = escapeSvgText(planLabelByType[type] || "Glavni prostor");
  const optionText = selectedLabels.length ? escapeSvgText(selectedLabels.join(" • ")) : "Bez dodatnih opcija";
  const parts = [];

  if (hasWindow) parts.push(exportWindow({ x: frame.x + frame.w * 0.42, y: frame.y + 14, w: Math.min(170, frame.w * 0.28) }));
  if (hasDoor) parts.push(exportDoor({ x: frame.x + 54, y: frame.y + frame.h - 16 }));

  if (type === "wc" || type === "bath") {
    parts.push(`<line class="partition" x1="${frame.x + frame.w - 230}" y1="${frame.y + 16}" x2="${frame.x + frame.w - 230}" y2="${frame.y + frame.h - 16}" />`);
    parts.push(`<text class="room-label" x="${frame.x + (frame.w - 230) / 2}" y="${frame.y + frame.h / 2}">${type === "bath" ? "Glavni prostor" : "Radni prostor"}</text>`);
    parts.push(`<text class="room-label" x="${frame.x + frame.w - 120}" y="${frame.y + 56}">${type === "bath" ? "Kupatilo" : "WC"}</text>`);
    parts.push(exportSanitary({ x: frame.x + frame.w - 120, y: frame.y + 130, label: "WC" }));
    if (type === "bath") parts.push(exportShower({ x: frame.x + frame.w - 62, y: frame.y + frame.h - 94 }));
  } else if (type === "doubleOffice") {
    parts.push(`<line class="partition" x1="${frame.x + frame.w / 2}" y1="${frame.y + 16}" x2="${frame.x + frame.w / 2}" y2="${frame.y + frame.h - 16}" />`);
    parts.push(`<text class="room-label" x="${frame.x + frame.w * 0.25}" y="${frame.y + frame.h / 2}">Kancelarija 1</text>`);
    parts.push(`<text class="room-label" x="${frame.x + frame.w * 0.75}" y="${frame.y + frame.h / 2}">Kancelarija 2</text>`);
  } else if (type === "showers") {
    [0, 1, 2].forEach((i) => {
      const zoneX = frame.x + 36 + i * ((frame.w - 72) / 3);
      const zoneW = (frame.w - 72) / 3;
      if (i > 0) parts.push(`<line class="partition" x1="${zoneX}" y1="${frame.y + 16}" x2="${zoneX}" y2="${frame.y + frame.h - 16}" />`);
      parts.push(exportShower({ x: zoneX + zoneW / 2, y: frame.y + frame.h / 2 - 28 }));
    });
  } else if (type === "multiSanitary") {
    [0, 1, 2, 3].forEach((i) => {
      const zoneX = frame.x + 26 + i * ((frame.w - 52) / 4);
      const zoneW = (frame.w - 52) / 4;
      if (i > 0) parts.push(`<line class="partition" x1="${zoneX}" y1="${frame.y + 16}" x2="${zoneX}" y2="${frame.y + frame.h - 16}" />`);
      parts.push(i % 2 === 0 ? exportSanitary({ x: zoneX + zoneW / 2, y: frame.y + 112, label: "WC" }) : exportShower({ x: zoneX + zoneW / 2, y: frame.y + 108 }));
    });
  } else if (type === "smallWc") {
    parts.push(exportSanitary({ x: frame.x + frame.w / 2, y: frame.y + 120, label: "WC" }));
  } else {
    parts.push(`<text class="room-label" x="${frame.x + frame.w / 2}" y="${frame.y + frame.h / 2}">${roomLabel}</text>`);
    if (type === "counter") parts.push(exportWindow({ x: frame.x + frame.w - 122, y: frame.y + frame.h / 2, w: 90 }));
    if (type === "storage") {
      parts.push(`
        <g class="shelves">
          <line x1="${frame.x + frame.w - 170}" y1="${frame.y + 55}" x2="${frame.x + frame.w - 170}" y2="${frame.y + frame.h - 55}" />
          <line x1="${frame.x + frame.w - 125}" y1="${frame.y + 55}" x2="${frame.x + frame.w - 125}" y2="${frame.y + frame.h - 55}" />
          <line x1="${frame.x + frame.w - 80}" y1="${frame.y + 55}" x2="${frame.x + frame.w - 80}" y2="${frame.y + frame.h - 55}" />
        </g>`);
    }
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1400" height="820" viewBox="0 0 1000 600" role="img" aria-label="${title}">
  <defs>
    <pattern id="grid-export" width="28" height="28" patternUnits="userSpaceOnUse">
      <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(16,84,214,.08)" stroke-width="1" />
    </pattern>
    <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="8" stdDeviation="10" flood-color="#0f172a" flood-opacity="0.12"/>
    </filter>
  </defs>
  <style>
    .page-bg{fill:#f8fafc}.title{font:800 28px Arial,sans-serif;fill:#0f172a}.subtitle{font:600 15px Arial,sans-serif;fill:#64748b}.caption{font:600 13px Arial,sans-serif;fill:#64748b}.outer{fill:#fff;stroke:#1054d6;stroke-width:4;filter:url(#soft-shadow)}.inner{fill:url(#grid-export);stroke:#94a3b8;stroke-width:1.5}.dimension line{stroke:#1054d6;stroke-width:2}.dimension text{font:700 14px Arial,sans-serif;fill:#1054d6}.door path{fill:none;stroke:#0f172a;stroke-width:3;stroke-linecap:round}.window line{stroke:#1054d6;stroke-width:5;stroke-linecap:round}.partition{stroke:#0f172a;stroke-width:3;stroke-dasharray:10 7}.room-label{font:800 20px Arial,sans-serif;fill:#0f172a;text-anchor:middle}.fixture circle,.fixture rect{fill:#eff6ff;stroke:#1054d6;stroke-width:2}.fixture path{fill:none;stroke:#1054d6;stroke-width:3}.fixture text{font:800 14px Arial,sans-serif;fill:#0f172a}.shelves line{stroke:#64748b;stroke-width:4}.footer-box{fill:#ffffff;stroke:#dbe4f0;stroke-width:1.5}.brand{font:900 16px Arial,sans-serif;fill:#1054d6;letter-spacing:2px}
  </style>
  <rect class="page-bg" x="0" y="0" width="1000" height="600" rx="24" />
  <text class="brand" x="55" y="50">RB TIM — KONFIGURACIJA</text>
  <text class="title" x="55" y="90">${title}</text>
  <text class="subtitle" x="55" y="118">Dimenzije: ${dimensions}</text>

  <rect class="outer" x="${frame.x}" y="${frame.y}" width="${frame.w}" height="${frame.h}" rx="6" />
  <rect class="inner" x="${frame.x + 16}" y="${frame.y + 16}" width="${frame.w - 32}" height="${frame.h - 32}" rx="4" />
  ${exportDimensionLine({ x1: frame.x, y1: frame.y - 34, x2: frame.x + frame.w, y2: frame.y - 34, label: wLabel })}
  ${exportDimensionLine({ x1: frame.x - 34, y1: frame.y, x2: frame.x - 34, y2: frame.y + frame.h, label: dLabel, orientation: "vertical" })}
  ${parts.join("\n  ")}

  <rect class="footer-box" x="55" y="515" width="890" height="48" rx="14" />
  <text class="caption" x="75" y="544">Izabrane opcije: ${optionText}</text>
  <text class="caption" x="500" y="582" text-anchor="middle">Informativni prikaz — finalne pozicije vrata, prozora i instalacija potvrđuju se pre izrade.</text>
</svg>`;
};

const PlanSketch = ({ layout }) => {
  const frame = getFrameByRatio(layout.plan?.ratio);
  const type = layout.plan?.type || "open";
  const hasDoor = layout.plan?.hasDoor !== false;
  const hasWindow = layout.plan?.hasWindow !== false;
  const isStorage = ["storage"].includes(type);
  const wLabel = layout.dimensions?.split("×")?.[0]?.trim() || "6,00 m";
  const dLabel = layout.dimensions?.split("×")?.[1]?.trim() || "2,40 m";
  const isWcLayout = type === "wc" || type === "smallWc";
  const isBathroomLayout = type === "bath";
  const fixtureBox = getFixtureBox(frame, layout.plan?.fixturePosition || "back-right", isBathroomLayout ? "bathroom" : "wc");
  const partitionX = Math.max(frame.x + frame.w * 0.56, fixtureBox.x - 18);

  const roomLabel = {
    open: "Otvoren prostor",
    living: "Boravak / soba",
    office: "Kancelarija",
    storage: "Magacin",
    workshop: "Radionica",
    guard: "Portir",
    counter: "Portirnica + šalter",
    doubleOffice: "Spojeni moduli",
  }[type] || "Glavni prostor";

  return (
    <svg className="tech-sketch builder-sketch" viewBox="0 0 1000 430" role="img" aria-label={`Tehnički crtež: ${layout.name}`}>
      <defs>
        <pattern id={`grid-${layout.key}`} width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(16,84,214,.08)" strokeWidth="1" />
        </pattern>
      </defs>

      <rect x="0" y="0" width="1000" height="430" rx="18" fill={`url(#grid-${layout.key})`} />
      <rect className="tech-sketch__outer" x={frame.x} y={frame.y} width={frame.w} height={frame.h} rx="4" />
      <rect className="tech-sketch__inner" x={frame.x + 16} y={frame.y + 16} width={frame.w - 32} height={frame.h - 32} rx="2" />

      <DimensionLine x1={frame.x} y1={frame.y - 34} x2={frame.x + frame.w} y2={frame.y - 34} label={wLabel} />
      <DimensionLine x1={frame.x - 34} y1={frame.y} x2={frame.x - 34} y2={frame.y + frame.h} label={dLabel} orientation="vertical" />

      {layout.plan?.ratio === "double" && (
        <line className="builder-sketch__seam" x1={frame.x + 16} y1={frame.y + frame.h / 2} x2={frame.x + frame.w - 16} y2={frame.y + frame.h / 2} />
      )}

      {hasDoor && (
        <WallFeature
          frame={frame}
          side={layout.plan?.doorSide || "front"}
          position={layout.plan?.doorPosition || 35}
          kind="door"
          label="Vrata"
        />
      )}

      {hasWindow && (
        <WallFeature
          frame={frame}
          side={layout.plan?.windowSide || "front"}
          position={layout.plan?.windowPosition || 65}
          kind="window"
          label="Prozor"
        />
      )}

      {(isWcLayout || isBathroomLayout) && (
        <>
          <line className="builder-sketch__partition" x1={partitionX} y1={frame.y + 16} x2={partitionX} y2={frame.y + frame.h - 16} />
          <text className="builder-sketch__micro-label" x={partitionX + 10} y={frame.y + 34}>Sanitarni blok</text>
          <g className="builder-sketch__fixture-block">
            <rect x={fixtureBox.x} y={fixtureBox.y} width={fixtureBox.w} height={fixtureBox.h} rx="12" />
            <text x={fixtureBox.x + fixtureBox.w / 2} y={fixtureBox.y + 30} textAnchor="middle">{isBathroomLayout ? "Kupatilo" : "WC"}</text>
            <circle cx={fixtureBox.x + fixtureBox.w * 0.36} cy={fixtureBox.y + fixtureBox.h * 0.62} r="18" />
            {isBathroomLayout && <rect x={fixtureBox.x + fixtureBox.w * 0.58} y={fixtureBox.y + fixtureBox.h * 0.44} width="48" height="48" rx="8" />}
          </g>
        </>
      )}

      {type === "doubleOffice" && (
        <>
          <line className="builder-sketch__partition" x1={frame.x + frame.w / 2} y1={frame.y + 16} x2={frame.x + frame.w / 2} y2={frame.y + frame.h - 16} />
          <text className="tech-sketch__room-label" x={frame.x + frame.w * 0.25} y={frame.y + frame.h / 2}>Kancelarija 1</text>
          <text className="tech-sketch__room-label" x={frame.x + frame.w * 0.75} y={frame.y + frame.h / 2}>Kancelarija 2</text>
        </>
      )}

      {type === "showers" && (
        <>
          {[0, 1, 2].map((i) => {
            const zoneX = frame.x + 36 + i * ((frame.w - 72) / 3);
            const zoneW = (frame.w - 72) / 3;
            return (
              <g key={i}>
                {i > 0 && <line className="builder-sketch__partition" x1={zoneX} y1={frame.y + 16} x2={zoneX} y2={frame.y + frame.h - 16} />}
                <ShowerIcon x={zoneX + zoneW / 2} y={frame.y + frame.h / 2 - 28} />
              </g>
            );
          })}
        </>
      )}

      {type === "multiSanitary" && (
        <>
          {[0, 1, 2, 3].map((i) => {
            const zoneX = frame.x + 26 + i * ((frame.w - 52) / 4);
            const zoneW = (frame.w - 52) / 4;
            return (
              <g key={i}>
                {i > 0 && <line className="builder-sketch__partition" x1={zoneX} y1={frame.y + 16} x2={zoneX} y2={frame.y + frame.h - 16} />}
                {i % 2 === 0 ? <SanitaryIcon x={zoneX + zoneW / 2} y={frame.y + 112} label="WC" /> : <ShowerIcon x={zoneX + zoneW / 2} y={frame.y + 108} />}
              </g>
            );
          })}
        </>
      )}

      {!["wc", "bath", "smallWc", "doubleOffice", "showers", "multiSanitary"].includes(type) && (
        <>
          <text className="tech-sketch__room-label" x={frame.x + frame.w / 2} y={frame.y + frame.h / 2}>{roomLabel}</text>
          {type === "counter" && (
            <WallFeature frame={frame} side="front" position={80} kind="window" label="Šalter" />
          )}
          {isStorage && (
            <g className="tech-sketch__shelves">
              <line x1={frame.x + frame.w - 170} y1={frame.y + 55} x2={frame.x + frame.w - 170} y2={frame.y + frame.h - 55} />
              <line x1={frame.x + frame.w - 125} y1={frame.y + 55} x2={frame.x + frame.w - 125} y2={frame.y + frame.h - 55} />
              <line x1={frame.x + frame.w - 80} y1={frame.y + 55} x2={frame.x + frame.w - 80} y2={frame.y + frame.h - 55} />
            </g>
          )}
        </>
      )}

      <text className="tech-sketch__caption" x="500" y="398" textAnchor="middle">
        Informativni prikaz rasporeda — precizne pozicije vrata, prozora i instalacija definišu se pre izrade.
      </text>
    </svg>
  );
};

const ModalShell = ({ open, onClose, eyebrow, title, lead, children, wide = false }) => {
  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.classList.add("rb-modal-open");
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.classList.remove("rb-modal-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="rb-modal-backdrop" onMouseDown={onClose} role="presentation">
      <div
        className={`rb-modal ${wide ? "rb-modal--wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <button type="button" className="rb-modal__close" onClick={onClose} aria-label="Zatvori prozor">
          ×
        </button>
        <div className="rb-modal__head">
          {eyebrow && <span className="eyebrow">{eyebrow}</span>}
          <h2>{title}</h2>
          {lead && <p>{lead}</p>}
        </div>
        {children}
      </div>
    </div>
  );
};

const DimensionsModal = ({ open, onClose }) => (
  <ModalShell
    open={open}
    onClose={onClose}
    eyebrow="Mere i dimenzije"
    title="Standardne mere RB TIM kontejnera"
    lead="Ovo su najčešće dimenzije iz tehničke osnove. Finalna mera, raspored vrata, prozora i instalacija se potvrđuju pre izrade. Cene nisu prikazane u ovom pregledu."
    wide
  >
    <div className="dimension-catalog">
      {DIMENSION_CATALOG.map((group) => (
        <div className="dimension-catalog__group" key={group.title}>
          <h3>{group.title}</h3>
          <div className="dimension-table-wrap">
            <table className="dimension-table">
              <thead>
                <tr>
                  <th>Model / varijanta</th>
                  <th>Spoljne dimenzije</th>
                  <th>Površina</th>
                  <th>Tipična namena</th>
                  <th>Napomena</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => (
                  <tr key={`${group.title}-${item.name}`}>
                    <td><strong>{item.name}</strong></td>
                    <td>{item.dimensions}</td>
                    <td>{item.area}</td>
                    <td>{item.purpose}</td>
                    <td>{item.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
    <div className="rb-modal__note">
      Standardna tehnička osnova za 6,00 × 2,40 × 2,70 m: unutrašnja mera približno 5,82 × 2,22 × 2,40 m, PVC vrata 1,00 × 2,05 m i PVC prozor 1,00 × 1,20 m.
    </div>
  </ModalShell>
);


const BUILDER_SIDES = [
  { key: "front", label: "Prednja strana" },
  { key: "back", label: "Zadnja strana" },
  { key: "left", label: "Leva bočna strana" },
  { key: "right", label: "Desna bočna strana" },
];

const BUILDER_POSITIONS = [
  { key: 20, label: "Levo / početak", short: "20%" },
  { key: 35, label: "Levo od sredine", short: "35%" },
  { key: 50, label: "Sredina", short: "50%" },
  { key: 65, label: "Desno od sredine", short: "65%" },
  { key: 80, label: "Desno / kraj", short: "80%" },
];

const FIXTURE_POSITIONS = [
  { key: "front-left", label: "Prednji levi ugao" },
  { key: "front-right", label: "Prednji desni ugao" },
  { key: "back-left", label: "Zadnji levi ugao" },
  { key: "back-right", label: "Zadnji desni ugao" },
];

const sideLabel = (side) => BUILDER_SIDES.find((item) => item.key === side)?.label || side;
const positionLabel = (position) => BUILDER_POSITIONS.find((item) => Number(item.key) === Number(position))?.label || `${position}%`;
const fixtureLabel = (position) => FIXTURE_POSITIONS.find((item) => item.key === position)?.label || position;
const positionPercent = (position) => Math.min(86, Math.max(14, Number(position) || 50));

const defaultBuilderControls = (preset) => ({
  partition: {
    orientation: preset?.ratio === "double" ? "horizontal" : "vertical",
    position: 50,
  },
  door: { side: "front", position: 35 },
  extraDoor: { side: "back", position: 65 },
  window: { side: "front", position: 65 },
  extraWindow: { side: "right", position: 50 },
  fixture: { position: "back-right" },
});

const getFeaturePlacement = (frame, side, position, kind = "door") => {
  const pct = positionPercent(position) / 100;
  const isDoor = kind === "door";
  const size = isDoor ? { w: 78, h: 24 } : { w: 116, h: 18 };

  if (side === "back") {
    return { x: frame.x + frame.w * pct - size.w / 2, y: frame.y - size.h + 8, w: size.w, h: size.h, horizontal: true };
  }

  if (side === "left") {
    return { x: frame.x - size.h + 8, y: frame.y + frame.h * pct - size.w / 2, w: size.h, h: size.w, horizontal: false };
  }

  if (side === "right") {
    return { x: frame.x + frame.w - 8, y: frame.y + frame.h * pct - size.w / 2, w: size.h, h: size.w, horizontal: false };
  }

  return { x: frame.x + frame.w * pct - size.w / 2, y: frame.y + frame.h - 8, w: size.w, h: size.h, horizontal: true };
};

const getFixtureBox = (frame, position, type = "wc") => {
  const isBath = type === "bathroom";
  const box = isBath ? { w: Math.min(210, frame.w * 0.28), h: Math.min(145, frame.h * 0.46) } : { w: Math.min(165, frame.w * 0.24), h: Math.min(118, frame.h * 0.38) };
  const pad = 28;
  const safePosition = typeof position === "string" ? position : "back-right";
  const isRight = safePosition.includes("right");
  const isBack = safePosition.includes("back");

  return {
    x: isRight ? frame.x + frame.w - box.w - pad : frame.x + pad,
    y: isBack ? frame.y + pad : frame.y + frame.h - box.h - pad,
    w: box.w,
    h: box.h,
  };
};

const WallFeature = ({ frame, side, position, kind = "door", label }) => {
  const placement = getFeaturePlacement(frame, side, position, kind);
  const displayLabel = label || (kind === "door" ? "Vrata" : "Prozor");

  return (
    <g className={`builder-sketch__feature builder-sketch__feature--${kind}`}>
      <rect x={placement.x} y={placement.y} width={placement.w} height={placement.h} rx="7" />
      <text
        x={placement.x + placement.w / 2}
        y={placement.y + placement.h / 2 + 4}
        textAnchor="middle"
        transform={placement.horizontal ? undefined : `rotate(-90 ${placement.x + placement.w / 2} ${placement.y + placement.h / 2})`}
      >
        {displayLabel}
      </text>
    </g>
  );
};

const BuilderSketch = ({ preset, selectedOptions, controls }) => {
  const frame = getFrameByRatio(preset?.ratio || "wide");
  const hasPartition = selectedOptions.includes("partition");
  const hasDoor = selectedOptions.includes("door");
  const hasExtraDoor = selectedOptions.includes("extra-door");
  const hasWindow = selectedOptions.includes("window");
  const hasExtraWindow = selectedOptions.includes("extra-window");
  const hasBathroom = selectedOptions.includes("bathroom");
  const hasWc = selectedOptions.includes("wc") || hasBathroom;
  const wLabel = preset?.dimensions?.split("×")?.[0]?.trim() || "6,00 m";
  const dLabel = preset?.dimensions?.split("×")?.[1]?.trim() || "2,40 m";
  const partitionX = frame.x + frame.w * (positionPercent(controls.partition.position) / 100);
  const partitionY = frame.y + frame.h * (positionPercent(controls.partition.position) / 100);
  const fixtureBox = getFixtureBox(frame, controls.fixture.position, hasBathroom ? "bathroom" : "wc");

  return (
    <svg className="tech-sketch builder-sketch" viewBox="0 0 1000 430" role="img" aria-label={`Konfigurator: ${preset?.name || "kontejner"}`}>
      <defs>
        <pattern id={`builder-grid-${preset?.key || "model"}`} width="28" height="28" patternUnits="userSpaceOnUse">
          <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(16,84,214,.08)" strokeWidth="1" />
        </pattern>
      </defs>

      <rect x="0" y="0" width="1000" height="430" rx="18" fill={`url(#builder-grid-${preset?.key || "model"})`} />
      <rect className="tech-sketch__outer" x={frame.x} y={frame.y} width={frame.w} height={frame.h} rx="4" />
      <rect className="tech-sketch__inner" x={frame.x + 16} y={frame.y + 16} width={frame.w - 32} height={frame.h - 32} rx="2" />

      <DimensionLine x1={frame.x} y1={frame.y - 34} x2={frame.x + frame.w} y2={frame.y - 34} label={wLabel} />
      <DimensionLine x1={frame.x - 34} y1={frame.y} x2={frame.x - 34} y2={frame.y + frame.h} label={dLabel} orientation="vertical" />

      {preset?.ratio === "double" && (
        <line className="builder-sketch__seam" x1={frame.x + 16} y1={frame.y + frame.h / 2} x2={frame.x + frame.w - 16} y2={frame.y + frame.h / 2} />
      )}

      {hasPartition && controls.partition.orientation === "vertical" && (
        <>
          <line className="builder-sketch__partition" x1={partitionX} y1={frame.y + 16} x2={partitionX} y2={frame.y + frame.h - 16} />
          <text className="builder-sketch__micro-label" x={partitionX + 10} y={frame.y + 34}>Pregradni zid {controls.partition.position}%</text>
        </>
      )}

      {hasPartition && controls.partition.orientation === "horizontal" && (
        <>
          <line className="builder-sketch__partition" x1={frame.x + 16} y1={partitionY} x2={frame.x + frame.w - 16} y2={partitionY} />
          <text className="builder-sketch__micro-label" x={frame.x + 28} y={partitionY - 10}>Pregradni zid {controls.partition.position}%</text>
        </>
      )}

      {hasDoor && <WallFeature frame={frame} side={controls.door.side} position={controls.door.position} kind="door" label="Vrata" />}
      {hasExtraDoor && <WallFeature frame={frame} side={controls.extraDoor.side} position={controls.extraDoor.position} kind="door" label="Vrata 2" />}
      {hasWindow && <WallFeature frame={frame} side={controls.window.side} position={controls.window.position} kind="window" label="Prozor" />}
      {hasExtraWindow && <WallFeature frame={frame} side={controls.extraWindow.side} position={controls.extraWindow.position} kind="window" label="Prozor 2" />}

      {hasWc && (
        <g className="builder-sketch__fixture-block">
          <rect x={fixtureBox.x} y={fixtureBox.y} width={fixtureBox.w} height={fixtureBox.h} rx="12" />
          <text x={fixtureBox.x + fixtureBox.w / 2} y={fixtureBox.y + 30} textAnchor="middle">{hasBathroom ? "Kupatilo" : "WC"}</text>
          <circle cx={fixtureBox.x + fixtureBox.w * 0.36} cy={fixtureBox.y + fixtureBox.h * 0.62} r="18" />
          {hasBathroom && <rect x={fixtureBox.x + fixtureBox.w * 0.58} y={fixtureBox.y + fixtureBox.h * 0.44} width="48" height="48" rx="8" />}
        </g>
      )}

      <text className="tech-sketch__caption" x="500" y="398" textAnchor="middle">
        Kontrolisana skica upita — finalni tehnički crtež i pozicije potvrđuje RB TIM pre izrade.
      </text>
    </svg>
  );
};

const exportBuilderFeature = ({ frame, side, position, kind = "door", label }) => {
  const placement = getFeaturePlacement(frame, side, position, kind);
  const safeLabel = escapeSvgText(label || (kind === "door" ? "Vrata" : "Prozor"));
  const className = kind === "door" ? "door-feature" : "window-feature";
  const rotation = placement.horizontal ? "" : ` transform="rotate(-90 ${placement.x + placement.w / 2} ${placement.y + placement.h / 2})"`;

  return `
    <g class="${className}">
      <rect x="${placement.x}" y="${placement.y}" width="${placement.w}" height="${placement.h}" rx="7" />
      <text x="${placement.x + placement.w / 2}" y="${placement.y + placement.h / 2 + 4}" text-anchor="middle"${rotation}>${safeLabel}</text>
    </g>`;
};

const createBuilderSvgAttachment = ({ preset, selectedOptions, controls, selectedLabels }) => {
  const baseFrame = getFrameByRatio(preset?.ratio || "wide");
  const frame = { ...baseFrame, y: baseFrame.y + 65 };
  const wLabel = preset?.dimensions?.split("×")?.[0]?.trim() || "6,00 m";
  const dLabel = preset?.dimensions?.split("×")?.[1]?.trim() || "2,40 m";
  const title = escapeSvgText(preset?.name || "RB TIM konfiguracija");
  const dimensions = escapeSvgText(preset?.dimensions || "");
  const optionText = selectedLabels.length ? escapeSvgText(selectedLabels.join(" • ")) : "Bez dodatnih opcija";
  const hasPartition = selectedOptions.includes("partition");
  const hasBathroom = selectedOptions.includes("bathroom");
  const hasWc = selectedOptions.includes("wc") || hasBathroom;
  const parts = [];

  if (preset?.ratio === "double") {
    parts.push(`<line class="seam" x1="${frame.x + 16}" y1="${frame.y + frame.h / 2}" x2="${frame.x + frame.w - 16}" y2="${frame.y + frame.h / 2}" />`);
  }

  if (hasPartition && controls.partition.orientation === "vertical") {
    const x = frame.x + frame.w * (positionPercent(controls.partition.position) / 100);
    parts.push(`<line class="partition" x1="${x}" y1="${frame.y + 16}" x2="${x}" y2="${frame.y + frame.h - 16}" />`);
    parts.push(`<text class="micro-label" x="${x + 10}" y="${frame.y + 34}">Pregradni zid ${controls.partition.position}%</text>`);
  }

  if (hasPartition && controls.partition.orientation === "horizontal") {
    const y = frame.y + frame.h * (positionPercent(controls.partition.position) / 100);
    parts.push(`<line class="partition" x1="${frame.x + 16}" y1="${y}" x2="${frame.x + frame.w - 16}" y2="${y}" />`);
    parts.push(`<text class="micro-label" x="${frame.x + 28}" y="${y - 10}">Pregradni zid ${controls.partition.position}%</text>`);
  }

  if (selectedOptions.includes("door")) parts.push(exportBuilderFeature({ frame, side: controls.door.side, position: controls.door.position, kind: "door", label: "Vrata" }));
  if (selectedOptions.includes("extra-door")) parts.push(exportBuilderFeature({ frame, side: controls.extraDoor.side, position: controls.extraDoor.position, kind: "door", label: "Vrata 2" }));
  if (selectedOptions.includes("window")) parts.push(exportBuilderFeature({ frame, side: controls.window.side, position: controls.window.position, kind: "window", label: "Prozor" }));
  if (selectedOptions.includes("extra-window")) parts.push(exportBuilderFeature({ frame, side: controls.extraWindow.side, position: controls.extraWindow.position, kind: "window", label: "Prozor 2" }));

  if (hasWc) {
    const box = getFixtureBox(frame, controls.fixture.position, hasBathroom ? "bathroom" : "wc");
    parts.push(`
      <g class="fixture-block">
        <rect x="${box.x}" y="${box.y}" width="${box.w}" height="${box.h}" rx="12" />
        <text x="${box.x + box.w / 2}" y="${box.y + 30}" text-anchor="middle">${hasBathroom ? "Kupatilo" : "WC"}</text>
        <circle cx="${box.x + box.w * 0.36}" cy="${box.y + box.h * 0.62}" r="18" />
        ${hasBathroom ? `<rect x="${box.x + box.w * 0.58}" y="${box.y + box.h * 0.44}" width="48" height="48" rx="8" />` : ""}
      </g>`);
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="1400" height="820" viewBox="0 0 1000 600" role="img" aria-label="${title}">
  <defs>
    <pattern id="grid-export" width="28" height="28" patternUnits="userSpaceOnUse">
      <path d="M 28 0 L 0 0 0 28" fill="none" stroke="rgba(16,84,214,.08)" stroke-width="1" />
    </pattern>
    <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="8" stdDeviation="10" flood-color="#102a5c" flood-opacity="0.14" />
    </filter>
  </defs>
  <style>
    .bg{fill:#f6f8fb}.panel{fill:#fff;stroke:#e2e8f0;filter:url(#soft-shadow)}.title{font-family:Arial,sans-serif;font-size:28px;font-weight:800;fill:#111827}.subtitle,.caption{font-family:Arial,sans-serif;font-size:15px;fill:#64748b}.outer{fill:#fff;stroke:#111827;stroke-width:5}.inner{fill:url(#grid-export);stroke:#dbe4f0;stroke-width:2}.dimension line{stroke:#1054d6;stroke-width:2}.dimension text,.micro-label{font-family:Arial,sans-serif;font-size:13px;font-weight:700;fill:#1054d6}.partition{stroke:#1054d6;stroke-width:8;stroke-linecap:round}.seam{stroke:#64748b;stroke-width:4;stroke-dasharray:10 10}.door-feature rect{fill:#1054d6;stroke:#083b99;stroke-width:2}.window-feature rect{fill:#e8f0ff;stroke:#1054d6;stroke-width:2}.door-feature text{font-family:Arial,sans-serif;font-size:12px;font-weight:800;fill:#fff}.window-feature text{font-family:Arial,sans-serif;font-size:12px;font-weight:800;fill:#1054d6}.fixture-block rect{fill:#f8fafc;stroke:#1054d6;stroke-width:3}.fixture-block circle{fill:none;stroke:#1054d6;stroke-width:3}.fixture-block text{font-family:Arial,sans-serif;font-size:15px;font-weight:800;fill:#111827}.footer-box{fill:#f8fafc;stroke:#e2e8f0}
  </style>
  <rect class="bg" width="1000" height="600" />
  <rect class="panel" x="35" y="35" width="930" height="530" rx="24" />
  <text class="title" x="55" y="84">${title}</text>
  <text class="subtitle" x="55" y="114">Dimenzije: ${dimensions}</text>
  <rect class="inner" x="${frame.x}" y="${frame.y}" width="${frame.w}" height="${frame.h}" rx="4" />
  <rect class="outer" x="${frame.x}" y="${frame.y}" width="${frame.w}" height="${frame.h}" rx="4" fill="none" />
  ${exportDimensionLine({ x1: frame.x, y1: frame.y - 34, x2: frame.x + frame.w, y2: frame.y - 34, label: wLabel })}
  ${exportDimensionLine({ x1: frame.x - 34, y1: frame.y, x2: frame.x - 34, y2: frame.y + frame.h, label: dLabel, orientation: "vertical" })}
  ${parts.join("\n")}
  <rect class="footer-box" x="55" y="515" width="890" height="48" rx="14" />
  <text class="caption" x="75" y="544">Izabrane opcije: ${optionText}</text>
  <text class="caption" x="500" y="582" text-anchor="middle">Informativna skica upita — finalni tehnički crtež potvrđuje RB TIM.</text>
</svg>`;
};

const convertSvgToPngDataUrl = (svg, width = 1400, height = 820) => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("PNG konverzija nije dostupna van browsera."));
  }

  return new Promise((resolve, reject) => {
    const image = new Image();
    const blob = new Blob([svg], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);

    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(image, 0, 0, width, height);
        URL.revokeObjectURL(url);
        resolve(canvas.toDataURL("image/png"));
      } catch (error) {
        URL.revokeObjectURL(url);
        reject(error);
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("SVG nije mogao da se pretvori u PNG."));
    };

    image.src = url;
  });
};

const buildBuilderSummary = ({ preset, selectedOptions, controls, selectedLabels }) => {
  const lines = [
    `Osnova: ${preset?.name || "nije definisano"}`,
    `Dimenzije: ${preset?.dimensions || "nije definisano"}`,
  ];

  if (selectedOptions.includes("partition")) {
    lines.push(`Pregradni zid: ${controls.partition.orientation === "vertical" ? "vertikalni" : "horizontalni"}, približno na ${controls.partition.position}% osnove`);
  }

  if (selectedOptions.includes("door")) {
    lines.push(`Vrata: ${sideLabel(controls.door.side)}, pozicija ${positionLabel(controls.door.position)}`);
  }

  if (selectedOptions.includes("extra-door")) {
    lines.push(`Dodatna vrata: ${sideLabel(controls.extraDoor.side)}, pozicija ${positionLabel(controls.extraDoor.position)}`);
  }

  if (selectedOptions.includes("window")) {
    lines.push(`Prozor: ${sideLabel(controls.window.side)}, pozicija ${positionLabel(controls.window.position)}`);
  }

  if (selectedOptions.includes("extra-window")) {
    lines.push(`Dodatni prozor: ${sideLabel(controls.extraWindow.side)}, pozicija ${positionLabel(controls.extraWindow.position)}`);
  }

  if (selectedOptions.includes("wc") || selectedOptions.includes("bathroom")) {
    lines.push(`${selectedOptions.includes("bathroom") ? "Kupatilo" : "WC"}: ${fixtureLabel(controls.fixture.position)}`);
  }

  lines.push(`Opcije: ${selectedLabels.length ? selectedLabels.join(", ") : "bez dodatnih opcija"}`);
  return lines;
};

const SelectField = ({ label, value, onChange, children }) => (
  <label className="builder-control-field">
    <span>{label}</span>
    <select value={value} onChange={(event) => onChange(event.target.value)}>
      {children}
    </select>
  </label>
);

const ConfigurationModal = ({ open, onClose, modelKey }) => {
  const navigate = useNavigate();
  const defaultPreset = CONFIGURATOR_LIBRARY.find((item) => item.modelKey === modelKey) || CONFIGURATOR_LIBRARY[0];
  const [activePresetKey, setActivePresetKey] = useState(defaultPreset?.key);
  const [selectedOptions, setSelectedOptions] = useState(["door", "window", "electro"]);
  const [controls, setControls] = useState(defaultBuilderControls(defaultPreset));
  const [isPreparing, setIsPreparing] = useState(false);

  useEffect(() => {
    if (!open) return;
    const nextPreset = CONFIGURATOR_LIBRARY.find((item) => item.modelKey === modelKey) || CONFIGURATOR_LIBRARY[0];
    setActivePresetKey(nextPreset?.key);
    setControls(defaultBuilderControls(nextPreset));
  }, [open, modelKey]);

  const activePreset = CONFIGURATOR_LIBRARY.find((item) => item.key === activePresetKey) || defaultPreset || CONFIGURATOR_LIBRARY[0];

  const handlePresetChange = (key) => {
    const nextPreset = CONFIGURATOR_LIBRARY.find((item) => item.key === key) || activePreset;
    setActivePresetKey(key);
    setControls(defaultBuilderControls(nextPreset));
  };

  const toggleOption = (key) => {
    setSelectedOptions((current) => {
      if (current.includes(key)) return current.filter((item) => item !== key);
      if (key === "wc") return [...current.filter((item) => item !== "bathroom"), key, "water"];
      if (key === "bathroom") return [...current.filter((item) => item !== "wc"), key, "water"];
      return [...current, key];
    });
  };

  const updateControl = (section, field, value) => {
    setControls((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: field === "position" && section !== "fixture" ? Number(value) : value,
      },
    }));
  };

  const groupedOptions = CONFIGURATOR_OPTIONS.reduce((acc, item) => {
    acc[item.group] = acc[item.group] || [];
    acc[item.group].push(item);
    return acc;
  }, {});

  const selectedLabels = CONFIGURATOR_OPTIONS
    .filter((item) => selectedOptions.includes(item.key))
    .map((item) => item.label);

  const builderSummary = useMemo(() => buildBuilderSummary({
    preset: activePreset,
    selectedOptions,
    controls,
    selectedLabels,
  }), [activePreset, selectedOptions, controls, selectedLabels]);

  const handleSendConfiguration = async () => {
    if (isPreparing) return;
    setIsPreparing(true);

    const svg = createBuilderSvgAttachment({ preset: activePreset, selectedOptions, controls, selectedLabels });
    let drawingAttachment = {
      filename: `rbtim-${filenameSafe(activePreset?.name)}.svg`,
      mimeType: "image/svg+xml",
      svg,
    };

    try {
      const pngDataUrl = await convertSvgToPngDataUrl(svg);
      drawingAttachment = {
        filename: `rbtim-${filenameSafe(activePreset?.name)}.png`,
        mimeType: "image/png",
        dataUrl: pngDataUrl,
        svgFallback: svg,
      };
    } catch (error) {
      console.warn("PNG nije napravljen, šalje se SVG crtež.", error);
    }

    const configurationRequest = {
      createdAt: new Date().toISOString(),
      sourcePath: window.location.pathname,
      modelPage: modelKey,
      model: activePreset?.name || "Konfiguracija",
      dimensions: activePreset?.dimensions || "",
      description: activePreset?.description || "",
      selectedOptions: selectedLabels,
      selectedOptionKeys: selectedOptions,
      builderControls: controls,
      builderSummary,
      drawing: {
        title: activePreset?.name || "Konfiguracija",
        dimensions: activePreset?.dimensions || "",
        layoutType: "controlled-builder-v2",
        ratio: activePreset?.ratio || "wide",
        hasDoor: selectedOptions.includes("door") || selectedOptions.includes("extra-door"),
        hasWindow: selectedOptions.includes("window") || selectedOptions.includes("extra-window"),
        hasPartition: selectedOptions.includes("partition"),
        fixture: selectedOptions.includes("bathroom") ? "bathroom" : selectedOptions.includes("wc") ? "wc" : "none",
      },
      drawingAttachment,
    };

    try {
      window.localStorage.setItem(
        "rbtim_configuration_request",
        JSON.stringify(configurationRequest)
      );
    } catch (error) {
      console.warn("Konfiguracija nije mogla da se sačuva u browseru.", error);
    }

    setIsPreparing(false);
    onClose();
    navigate("/kontakt?konfiguracija=1");
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      eyebrow="Konfiguracija pre izrade"
      title="Složite osnovni zahtev za kontejner"
      lead="Ovo je kontrolisan konfigurator za upit: birate osnovu, dodajete zidove, vrata, prozore i sanitarije. RB TIM zatim potvrđuje da li je raspored tehnički izvodljiv."
      wide
    >
      <div className="builder-modal-grid builder-modal-grid--v2">
        <aside className="builder-sidebar">
          <h3>Modeli / mere</h3>
          <div className="builder-preset-list">
            {CONFIGURATOR_LIBRARY.map((item) => (
              <button
                type="button"
                key={item.key}
                className={`builder-preset ${activePreset?.key === item.key ? "is-active" : ""}`}
                onClick={() => handlePresetChange(item.key)}
              >
                <strong>{item.name}</strong>
                <span>{item.dimensions}</span>
              </button>
            ))}
          </div>
        </aside>

        <div className="builder-board">
          <div className="builder-board__top">
            <div>
              <span className="eyebrow">Kontrolisana skica</span>
              <h3>{activePreset?.name}</h3>
              <p>{activePreset?.description}</p>
            </div>
            <div className="builder-board__measure">
              <span>Dimenzije</span>
              <strong>{activePreset?.dimensions}</strong>
            </div>
          </div>

          <div className="builder-preview">
            <BuilderSketch preset={activePreset} selectedOptions={selectedOptions} controls={controls} />
          </div>

          <div className="builder-summary builder-summary--stacked">
            <div>
              <span>Opis konfiguracije</span>
              <strong>{builderSummary.join(" • ")}</strong>
            </div>
            <button type="button" className="btn btn-primary" onClick={handleSendConfiguration} disabled={isPreparing}>
              {isPreparing ? "Priprema crteža..." : "Pošaljite konfiguraciju"} <ArrowRight size={16} />
            </button>
          </div>
        </div>

        <aside className="builder-options builder-options--v2">
          <h3>Elementi</h3>
          {Object.entries(groupedOptions).map(([group, options]) => (
            <div className="builder-option-group" key={group}>
              <h4>{group}</h4>
              {options.map((option) => (
                <label className="builder-option" key={option.key}>
                  <input
                    type="checkbox"
                    checked={selectedOptions.includes(option.key)}
                    onChange={() => toggleOption(option.key)}
                  />
                  <span>
                    <strong>{option.label}</strong>
                    <small>{option.note}</small>
                  </span>
                </label>
              ))}
            </div>
          ))}

          <div className="builder-control-panel">
            <h4>Pozicije elemenata</h4>

            {selectedOptions.includes("partition") && (
              <div className="builder-control-box">
                <strong>Pregradni zid</strong>
                <SelectField label="Smer zida" value={controls.partition.orientation} onChange={(value) => updateControl("partition", "orientation", value)}>
                  <option value="vertical">Vertikalno — deli prostor levo/desno</option>
                  <option value="horizontal">Horizontalno — deli prostor napred/nazad</option>
                </SelectField>
                <SelectField label="Pozicija" value={controls.partition.position} onChange={(value) => updateControl("partition", "position", value)}>
                  {BUILDER_POSITIONS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
              </div>
            )}

            {selectedOptions.includes("door") && (
              <div className="builder-control-box">
                <strong>Vrata</strong>
                <SelectField label="Strana" value={controls.door.side} onChange={(value) => updateControl("door", "side", value)}>
                  {BUILDER_SIDES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
                <SelectField label="Pozicija" value={controls.door.position} onChange={(value) => updateControl("door", "position", value)}>
                  {BUILDER_POSITIONS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
              </div>
            )}

            {selectedOptions.includes("extra-door") && (
              <div className="builder-control-box">
                <strong>Dodatna vrata</strong>
                <SelectField label="Strana" value={controls.extraDoor.side} onChange={(value) => updateControl("extraDoor", "side", value)}>
                  {BUILDER_SIDES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
                <SelectField label="Pozicija" value={controls.extraDoor.position} onChange={(value) => updateControl("extraDoor", "position", value)}>
                  {BUILDER_POSITIONS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
              </div>
            )}

            {selectedOptions.includes("window") && (
              <div className="builder-control-box">
                <strong>Prozor</strong>
                <SelectField label="Strana" value={controls.window.side} onChange={(value) => updateControl("window", "side", value)}>
                  {BUILDER_SIDES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
                <SelectField label="Pozicija" value={controls.window.position} onChange={(value) => updateControl("window", "position", value)}>
                  {BUILDER_POSITIONS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
              </div>
            )}

            {selectedOptions.includes("extra-window") && (
              <div className="builder-control-box">
                <strong>Dodatni prozor</strong>
                <SelectField label="Strana" value={controls.extraWindow.side} onChange={(value) => updateControl("extraWindow", "side", value)}>
                  {BUILDER_SIDES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
                <SelectField label="Pozicija" value={controls.extraWindow.position} onChange={(value) => updateControl("extraWindow", "position", value)}>
                  {BUILDER_POSITIONS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
              </div>
            )}

            {(selectedOptions.includes("wc") || selectedOptions.includes("bathroom")) && (
              <div className="builder-control-box">
                <strong>{selectedOptions.includes("bathroom") ? "Kupatilo" : "WC"}</strong>
                <SelectField label="Pozicija" value={controls.fixture.position} onChange={(value) => updateControl("fixture", "position", value)}>
                  {FIXTURE_POSITIONS.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                </SelectField>
              </div>
            )}
          </div>
        </aside>
      </div>
    </ModalShell>
  );
};


const TechnicalSection = ({ model, technical }) => {
  const [activeKey, setActiveKey] = useState(technical.layouts?.[0]?.key);
  const [openModal, setOpenModal] = useState(null);
  const activeLayout = technical.layouts?.find((item) => item.key === activeKey) || technical.layouts?.[0];

  if (!activeLayout) return null;

  return (
    <section className="section section--gray" id="tehnicki-pregled">
      <div className="container-rb">
        <div className="technical-panel">
          <div className="technical-panel__intro">
            <div>
              <span className="eyebrow">{technical.eyebrow}</span>
              <h2>{technical.title}</h2>
              <p>{technical.intro}</p>
            </div>
            <div className="technical-panel__quick">
              <button type="button" className="technical-quick-button" onClick={() => setOpenModal("dimensions")}>
                <Ruler size={18} /> Mere i raspored po dogovoru
              </button>
              <button type="button" className="technical-quick-button" onClick={() => setOpenModal("configuration")}>
                <Wrench size={18} /> Opcije se definišu pre izrade
              </button>
            </div>
          </div>

          <div className="spec-grid">
            {technical.keySpecs.map((spec) => (
              <div className="spec-card" key={`${model.key}-${spec.label}`}>
                <span>{spec.label}</span>
                <strong>{spec.value}</strong>
              </div>
            ))}
          </div>

          <div className="tech-layout">
            <div className="tech-layout__top">
              <div>
                <span className="eyebrow">Varijante rasporeda</span>
                <h3>{activeLayout.name}</h3>
                <p>{activeLayout.description}</p>
              </div>
              <div className="layout-tabs" role="tablist" aria-label={`${model.title} varijante rasporeda`}>
                {technical.layouts.map((layout) => (
                  <button
                    type="button"
                    key={layout.key}
                    className={`layout-tab ${layout.key === activeLayout.key ? "active" : ""}`}
                    onClick={() => setActiveKey(layout.key)}
                    role="tab"
                    aria-selected={layout.key === activeLayout.key}
                    data-testid={`layout-tab-${model.key}-${layout.key}`}
                  >
                    {layout.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="layout-meta-grid">
              <div className="layout-meta"><span>Dimenzije</span><strong>{activeLayout.dimensions}</strong></div>
              <div className="layout-meta"><span>Površina</span><strong>{activeLayout.area}</strong></div>
              <div className="layout-meta"><span>Namena</span><strong>{activeLayout.purpose}</strong></div>
            </div>

            <div className="tech-layout__drawing-wrap">
              <PlanSketch layout={activeLayout} />
            </div>
          </div>

          <div className="technical-bottom-grid">
            <div className="technical-checklist">
              <div className="technical-checklist__head">
                <ClipboardList size={20} />
                <h3>Šta se definiše kroz upit</h3>
              </div>
              <ul className="bullet-list">
                {technical.included.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </div>
            <div className="technical-warning">
              <Layers3 size={24} />
              <h3>Nemoj zaključavati raspored napamet</h3>
              <p>
                Ovo su najčešće varijante. Realan raspored zavisi od namene, instalacija, pozicije priključaka, transporta i mesta postavljanja. Zato je forma / poziv obavezan pre finalne ponude.
              </p>
              <a href="/kontakt" className="btn btn-primary">Pošaljite upit <ArrowRight size={16} /></a>
            </div>
          </div>
        </div>
      </div>

      <DimensionsModal open={openModal === "dimensions"} onClose={() => setOpenModal(null)} />
      <ConfigurationModal open={openModal === "configuration"} onClose={() => setOpenModal(null)} modelKey={model.key} />
    </section>
  );
};

const ModelDetail = ({ slug }) => {
  const model = MODELS.find((m) => m.slug === slug);
  const navigate = useNavigate();

  if (!model) {
    return (
      <section className="section">
        <div className="container-rb">
          <h1>Model nije pronađen</h1>
          <button onClick={() => navigate("/modeli-kontejnera")} className="btn btn-primary">Svi modeli</button>
        </div>
      </section>
    );
  }

  const technical = TECHNICAL_INFO[model.key];

  return (
    <>
      <SEO />
      <PageHero
        crumbs={[
          { label: "Početna", to: "/" },
          { label: "Modeli kontejnera", to: "/modeli-kontejnera" },
          { label: model.title },
        ]}
        title={model.title}
        lead={model.long}
      />

      <section className="section" data-testid={`model-detail-${model.key}`}>
        <div className="container-rb model-intro-grid">
          <div className="model-intro-media" data-testid={`model-media-${model.key}`}>
            <SafeImage src={model.image} alt={model.title} label={model.title} loading="lazy" />
          </div>
          <div className="model-intro-copy">
            <span className="eyebrow">{model.title}</span>
            <h2>Praktično rešenje za konkretnu namenu</h2>
            <p>{model.long}</p>
            <ul className="bullet-list">
              {model.benefits.slice(0, 4).map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
            <div className="model-intro-actions">
              <a href="#tehnicki-pregled" className="btn btn-primary" data-testid={`model-cta-tech-${model.key}`}>
                Pogledajte rasporede <ArrowRight size={16} />
              </a>
              <a href="/kontakt" className="btn btn-outline" data-testid={`model-cta-contact-${model.key}`}>
                Pošaljite upit
              </a>
            </div>
          </div>
        </div>
      </section>

      {technical && <TechnicalSection model={model} technical={technical} />}

      <section className="section">
        <div className="container-rb">
          <SectionHeader eyebrow="Prednosti" title="Ključne prednosti" lead={`Šta dobijate sa modelom „${model.title}”.`} />
          <div className="grid-3">
            {model.benefits.map((b, i) => (
              <div key={b} className="benefit-card" data-testid={`model-benefit-${model.key}-${i}`}>
                <div className="benefit-card__icon"><CheckCircle2 size={22} /></div>
                <h3>{b}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--gray">
        <div className="container-rb">
          <SectionHeader eyebrow="Primena" title="Gde se koristi" lead="Tipične namene ovog modela." />
          <div className="usecase-grid">
            {model.useCases.map((u, i) => (
              <div key={u} className="usecase" data-testid={`model-usecase-${model.key}-${i}`}>{u}</div>
            ))}
          </div>
        </div>
      </section>

      <section id="galerija" className="section">
        <div className="container-rb">
          <SectionHeader eyebrow="Galerija" title="Primeri" lead="Nekoliko slika ovog modela." />
          <div className="gallery-grid">
            {model.images.slice(0, 6).map((src, i) => (
              <div key={i} className="gallery-item" data-testid={`model-gallery-${model.key}-${i}`}>
                <SafeImage src={src} alt={`${model.title} ${i + 1}`} label={model.title} loading="lazy" />
                <span className="gallery-item__label">{model.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default ModelDetail;
